import React from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { SelectBox } from 'src/components/SelectBox';
import {
  FieldOperators,
  Insight,
  InsightType,
} from 'src/interfaces/agent.interface';
import { HumanDetectionResult } from 'src/interfaces/call.interface';
import {
  UiCondition,
  UiConditionType,
  CallCondition,
  PostCallContainsCondition,
  PostCallDataExtractionCondition,
  PostCallJsonPropertyCondition,
  PostCallConditionsMode,
} from './types';
import { Badge } from 'src/components/Badge';
import {
  allowedOperatorsByInsightType,
  sortInsightsForSelect,
} from './postCallDataExtractionRules';

type ConditionItemProps = {
  condition: UiCondition;
  onChange: (next: UiCondition) => void;
  onDelete: () => void;
  onSelectType: (nextType: UiConditionType) => void;
  disabled?: boolean;
  mode?: PostCallConditionsMode;
  insights?: Insight[];
};

const operatorLabelMap: Record<FieldOperators, string> = {
  [FieldOperators.EQ]: 'Equals',
  [FieldOperators.NEQ]: 'Does not equal',
  [FieldOperators.GT]: 'Greater than',
  [FieldOperators.GTE]: 'Greater than or equal to',
  [FieldOperators.LT]: 'Less than',
  [FieldOperators.LTE]: 'Less than or equal to',
  [FieldOperators.CONTAINS]: 'Contains',
  [FieldOperators.NOT_CONTAINS]: 'Does not contain',
};

const operatorOptions = Object.values(FieldOperators).map((op) => ({
  value: op,
  label: operatorLabelMap[op as FieldOperators],
}));

const getOperatorOptions = (ops: FieldOperators[]) =>
  ops.map((op) => ({ value: op, label: operatorLabelMap[op] }));

export const ConditionItem: React.FC<ConditionItemProps> = ({
  condition,
  onChange,
  onDelete,
  onSelectType,
  disabled = false,
  mode = PostCallConditionsMode.PROMPT,
  insights = [],
}) => {
  if (condition.type === UiConditionType.UNSELECTED) {
    return (
      <div className="rounded-lg border border-neutral-200 p-4 bg-neutral-0">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-neutral-500">
            Select a condition type
          </div>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={onDelete}
            disabled={disabled}
            className="hover:text-error"
          >
            <X size={16} />
          </Button>
        </div>
        <div className="flex justify-center gap-3 flex-wrap">
          <Button
            size="small"
            className="w-48 whitespace-nowrap"
            onClick={() => onSelectType(UiConditionType.CALL)}
            disabled={disabled}
          >
            Call
          </Button>
          <Button
            size="small"
            className="w-48 whitespace-nowrap"
            onClick={() =>
              onSelectType(
                mode === PostCallConditionsMode.DATA_EXTRACTION
                  ? UiConditionType.POST_CALL_DATA_EXTRACTION
                  : UiConditionType.POST_CALL_CONTAINS,
              )
            }
            disabled={disabled}
          >
            {mode === PostCallConditionsMode.DATA_EXTRACTION
              ? 'Call Insight'
              : 'Post-Call Analysis'}
          </Button>
        </div>
      </div>
    );
  }

  if (
    mode === PostCallConditionsMode.DATA_EXTRACTION &&
    condition.type === UiConditionType.POST_CALL_DATA_EXTRACTION
  ) {
    const dataExtraction = condition as PostCallDataExtractionCondition;
    const sortedInsights = sortInsightsForSelect(insights);
    const selectedInsight =
      insights.find((i) => i.id === dataExtraction.analysisItemId) ?? null;

    const extractedDataOptions = sortedInsights.map((i) => ({
      value: i.id,
      label: i.label || i.id,
    }));

    const selectedInsightType = selectedInsight?.type;
    const allowedOperators = selectedInsightType
      ? allowedOperatorsByInsightType[selectedInsightType]
      : [];

    const operatorOptionsForItem = getOperatorOptions(allowedOperators);

    return (
      <div className="rounded-lg border border-neutral-200 p-4 bg-neutral-0 flex items-center gap-3">
        <Badge
          variant="primary"
          className="text-md px-4 font-normal whitespace-nowrap"
        >
          Call Insight
        </Badge>

        <div
          className={
            selectedInsightType === InsightType.BOOLEAN
              ? 'flex flex-wrap items-center gap-3 flex-1'
              : 'grid grid-cols-1 md:grid-cols-3 gap-3 items-center flex-1'
          }
        >
          <div
            className={
              selectedInsightType === InsightType.BOOLEAN ? 'w-64' : ''
            }
          >
            <SelectBox
              value={
                dataExtraction.analysisItemId
                  ? {
                      value: dataExtraction.analysisItemId,
                      label:
                        extractedDataOptions.find(
                          (o) => o.value === dataExtraction.analysisItemId,
                        )?.label ?? dataExtraction.analysisItemId,
                    }
                  : null
              }
              options={extractedDataOptions}
              onChange={(val) =>
                onChange({
                  ...dataExtraction,
                  analysisItemId: String(val),
                  operator: undefined,
                  right: null,
                } as PostCallDataExtractionCondition)
              }
              disabled={disabled || extractedDataOptions.length === 0}
            />
          </div>

          {selectedInsightType === InsightType.BOOLEAN ? (
            <div className="flex items-center gap-3">
              <div className="text-neutral-600 font-medium">is</div>
              <div className="w-64">
                <SelectBox
                  value={
                    typeof dataExtraction.right === 'boolean'
                      ? {
                          value: dataExtraction.right ? 'true' : 'false',
                          label: dataExtraction.right ? 'Yes' : 'No',
                        }
                      : null
                  }
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  onChange={(val) =>
                    onChange({
                      ...dataExtraction,
                      operator: FieldOperators.EQ,
                      right: val === 'true',
                    } as PostCallDataExtractionCondition)
                  }
                  disabled={disabled}
                />
              </div>
            </div>
          ) : (
            <>
              <div>
                <SelectBox
                  value={
                    dataExtraction.operator
                      ? {
                          value: dataExtraction.operator,
                          label: operatorLabelMap[dataExtraction.operator],
                        }
                      : null
                  }
                  options={operatorOptionsForItem}
                  onChange={(val) =>
                    onChange({
                      ...dataExtraction,
                      operator: val as FieldOperators,
                    } as PostCallDataExtractionCondition)
                  }
                  disabled={disabled}
                />
              </div>

              <div>
                {selectedInsightType === InsightType.ENUM ? (
                  <SelectBox
                    value={
                      typeof dataExtraction.right === 'string' &&
                      dataExtraction.right
                        ? {
                            value: dataExtraction.right,
                            label: String(dataExtraction.right).toLowerCase(),
                          }
                        : null
                    }
                    options={(selectedInsight?.enum_options ?? [])
                      .slice()
                      .sort((a, b) =>
                        a.toLowerCase().localeCompare(b.toLowerCase()),
                      )
                      .map((opt) => ({
                        value: opt,
                        label: opt.toLowerCase(),
                      }))}
                    onChange={(val) =>
                      onChange({
                        ...dataExtraction,
                        right: String(val),
                      } as PostCallDataExtractionCondition)
                    }
                    disabled={
                      disabled || !(selectedInsight?.enum_options?.length ?? 0)
                    }
                  />
                ) : (
                  <Input
                    id={`${condition.id}-extracted-value`}
                    placeholder="Value to compare"
                    value={
                      typeof dataExtraction.right === 'string'
                        ? dataExtraction.right
                        : ''
                    }
                    onChange={(e) =>
                      onChange({
                        ...dataExtraction,
                        right: e.target.value,
                      } as PostCallDataExtractionCondition)
                    }
                    disabled={disabled}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <Button
          variant="text"
          color="secondary"
          size="small"
          onClick={onDelete}
          disabled={disabled}
          className="hover:text-error"
        >
          <X size={16} />
        </Button>
      </div>
    );
  }

  if (condition.type === UiConditionType.CALL) {
    const value = (condition as CallCondition).humanDetectionResult;
    return (
      <div className="rounded-lg border border-neutral-200 p-4 bg-neutral-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Badge variant="primary" className="text-md px-4 font-normal">
              Call
            </Badge>
            <div className="w-64">
              <SelectBox
                options={[
                  {
                    value: HumanDetectionResult.NO_HUMAN,
                    label: 'Went to voicemail',
                  },
                  {
                    value: HumanDetectionResult.HUMAN,
                    label: 'Did not go to voicemail',
                  },
                ]}
                value={{
                  value,
                  label:
                    value === HumanDetectionResult.NO_HUMAN
                      ? 'Went to voicemail'
                      : 'Did not go to voicemail',
                }}
                onChange={(next) =>
                  onChange({
                    ...condition,
                    humanDetectionResult: next as HumanDetectionResult,
                  } as CallCondition)
                }
                disabled={disabled}
              />
            </div>
          </div>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={onDelete}
            disabled={disabled}
            className="hover:text-error"
          >
            <X size={16} />
          </Button>
        </div>
      </div>
    );
  }

  if (
    condition.type === UiConditionType.POST_CALL_CONTAINS ||
    condition.type === UiConditionType.POST_CALL_JSON_PROPERTY
  ) {
    const isContains = condition.type === UiConditionType.POST_CALL_CONTAINS;
    const contains = condition as PostCallContainsCondition;
    const jsonProp = condition as PostCallJsonPropertyCondition;

    const analysisTypeOption = isContains
      ? { value: 'contains', label: 'Contains text' }
      : { value: 'property', label: 'Matches field value' };

    return (
      <div className="rounded-lg border border-neutral-200 p-4 bg-neutral-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Badge variant="primary" className="text-md px-4 font-normal">
              Post-Call Analysis
            </Badge>
            <div className="w-64">
              <SelectBox
                value={analysisTypeOption}
                options={[
                  { value: 'contains', label: 'Contains text' },
                  { value: 'property', label: 'Matches field value' },
                ]}
                onChange={(val) => {
                  if (val === 'contains' && !isContains) {
                    onChange({
                      id: condition.id,
                      type: UiConditionType.POST_CALL_CONTAINS,
                      searchString: '',
                    } as PostCallContainsCondition);
                  } else if (val === 'property' && isContains) {
                    onChange({
                      id: condition.id,
                      type: UiConditionType.POST_CALL_JSON_PROPERTY,
                      path: '',
                      operator: FieldOperators.EQ,
                      value: '',
                    } as PostCallJsonPropertyCondition);
                  }
                }}
                disabled={disabled}
              />
            </div>
          </div>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={onDelete}
            disabled={disabled}
            className="hover:text-error"
          >
            <X size={16} />
          </Button>
        </div>

        {isContains ? (
          <Input
            id={`${condition.id}-contains`}
            label="Search text for"
            placeholder="Enter search text"
            value={contains.searchString}
            onChange={(e) =>
              onChange({ ...contains, searchString: e.target.value })
            }
            disabled={disabled}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              id={`${condition.id}-json-path`}
              label="Field"
              placeholder="e.g., sentiment.score"
              value={jsonProp.path}
              onChange={(e) => onChange({ ...jsonProp, path: e.target.value })}
              disabled={disabled}
            />
            <div>
              <div className="mb-2 text-md text-neutral-600">Operator</div>
              <SelectBox
                value={
                  jsonProp.operator
                    ? {
                        value: jsonProp.operator,
                        label: operatorLabelMap[jsonProp.operator],
                      }
                    : null
                }
                options={operatorOptions}
                onChange={(val) =>
                  onChange({ ...jsonProp, operator: val as FieldOperators })
                }
                disabled={disabled}
              />
            </div>
            <Input
              id={`${condition.id}-json-value`}
              label="Value"
              placeholder="Value to compare"
              value={jsonProp.value}
              onChange={(e) => onChange({ ...jsonProp, value: e.target.value })}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    );
  }

  return null;
};
